import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, MapPin, FileText, Clock } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import Footer from "@/components/ledger/Footer";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";
import DownloadBrochureButton from "@/components/ledger/DownloadBrochureButton";
import { CORRIDORS, buildCorridorJsonLd } from "./corridors.mjs";

const SITE = "https://www.viloraimpex.com";

export default function CorridorPage() {
  const { slug } = useParams();
  const c = CORRIDORS[slug];

  useSeo({
    title: c ? `Basmati Rice Export from India to ${c.country} | Vilora Impex` : "Vilora Impex",
    description: c
      ? `Basmati rice export from India to ${c.country} — ${c.regulator}, ${c.arrivalPort} (${c.transitDays} days transit). ${c.tradeAgreement}. Halal: ${c.halalRequired ? "required" : "optional"}. FSSAI/APEDA registered.`
      : undefined,
    path: c ? `/basmati-rice/export-from-india-to-${slug}` : "/",
    image: `${SITE}/hero-port.jpg`,
    imageAlt: c ? `Indian basmati rice export to ${c.country} — Vilora Impex` : "Vilora Impex",
    ogType: "article",
    jsonLd: c ? buildCorridorJsonLd(slug) : null,
  });

  if (!c) {
    return (
      <main className="ledger relative min-h-screen">
        <Navbar />
        <section className="paper" style={{ padding: "80px 24px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <h1 className="display" style={{ fontSize: 40 }}>Country corridor not found</h1>
            <p style={{ marginTop: 12 }}>
              <Link to="/" className="story-back-link">Back to home</Link>
            </p>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="ledger corridor-page relative min-h-screen" data-testid="corridor-page">
      <CorridorStyles />
      <ScrollProgress />
      <Navbar />

      <section className="corridor-hero paper">
        <div className="corridor-shell">
          <nav className="corridor-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">·</span>
            <Link to="/basmati-rice-exporter-from-india">Basmati Rice</Link>
            <span aria-hidden="true">·</span>
            <span aria-current="page">Export to {c.country}</span>
          </nav>

          <div className="corridor-folio">
            <span className="folio">CORRIDOR</span>
            <span className="rule rule-grow" />
            <span className="folio" style={{ color: "var(--ink-mute)" }}>{c.region.toUpperCase()}</span>
          </div>

          <div className="corridor-flag-row">
            <span className="corridor-flag" aria-hidden="true">{c.flag}</span>
            <span className="brass-ribbon corridor-eyebrow">India &rarr; {c.country}</span>
          </div>

          <h1 className="display corridor-h1">Basmati Rice Export from India to {c.country}</h1>

          <p className="corridor-standfirst">
            Vilora Impex exports premium Indian basmati rice to {c.country} from {c.loadingPort} via {c.arrivalPort}.
            {" "}Transit time {c.transitDays} days. {c.regulator} compliance.
            {" "}{c.tradeAgreement}. {c.halalRequired ? "Halal certification required from MOIAT/SFDA/JAKIM-accepted bodies." : "Halal certification optional, relevant for specific diaspora retail channels."}
          </p>

          <div className="corridor-meta-row">
            <span className="corridor-meta-item">
              <MapPin size={13} strokeWidth={1.6} />
              <span>{c.arrivalPort}</span>
            </span>
            <span className="corridor-meta-item">
              <Clock size={13} strokeWidth={1.6} />
              <span>{c.transitDays} days from Mundra</span>
            </span>
            <span className="corridor-meta-item">
              <FileText size={13} strokeWidth={1.6} />
              <span>{c.tradeAgreement}</span>
            </span>
          </div>

          <div className="corridor-actions">
            <a href="mailto:exports@viloraimpex.com" className="btn-primary">
              <span className="btn-code">DESK</span>
              <span>Open a {c.country} inquiry</span>
              <span className="btn-arrow"><Mail size={14} strokeWidth={1.6} /></span>
            </a>
            <DownloadBrochureButton variant="ghost" />
          </div>
        </div>
      </section>

      <section className="corridor-fact-grid paper">
        <div className="corridor-shell">
          <dl className="corridor-facts">
            <div className="corridor-fact"><dt>Loading port (India)</dt><dd>{c.loadingPort}</dd></div>
            <div className="corridor-fact"><dt>Arrival port ({c.country})</dt><dd>{c.arrivalPort}</dd></div>
            <div className="corridor-fact"><dt>Sea transit</dt><dd>{c.transitDays} days</dd></div>
            <div className="corridor-fact"><dt>Lead time, PI to warehouse</dt><dd>{c.leadTimePIToWarehouse}</dd></div>
            <div className="corridor-fact"><dt>Regulator</dt><dd>{c.regulator}</dd></div>
            <div className="corridor-fact"><dt>Trade agreement</dt><dd>{c.tradeAgreement}</dd></div>
            <div className="corridor-fact"><dt>Currency</dt><dd>{c.currency}</dd></div>
            <div className="corridor-fact"><dt>Halal certification</dt><dd>{c.halalRequired ? "Required" : "Optional"}</dd></div>
            <div className="corridor-fact"><dt>Typical MOQ</dt><dd>{c.typicalMOQ}</dd></div>
            <div className="corridor-fact"><dt>Payment terms</dt><dd>{c.paymentTerms}</dd></div>
          </dl>
        </div>
      </section>

      <section className="corridor-section paper">
        <div className="corridor-shell">
          <h2 className="display">{c.country} regulatory landscape</h2>
          <p>Imports of Indian basmati rice into {c.country} are regulated by <strong>{c.regulator}</strong>. {c.dutyNote}</p>
          {c.halalRequired ? (
            <p><strong>Halal certification</strong> is mandatory for retail SKUs. Accepted bodies: {c.halalAcceptedBodies}.</p>
          ) : (
            <p>Halal certification is not mandatory across {c.country} retail broadly, but it is required or strongly preferred by specific diaspora retail channels. {c.halalAcceptedBodies}</p>
          )}
          <p><strong>Labelling:</strong> {c.labellingLanguages}.</p>
          {c.docExtras ? <p><strong>Documentation specific to {c.country}:</strong> {c.docExtras}.</p> : null}
        </div>
      </section>

      <section className="corridor-section corridor-section--tint paper">
        <div className="corridor-shell">
          <h2 className="display">Preferred basmati variety for {c.country}</h2>
          <p>{c.preferredVariety}.</p>
          <p>{c.marketNotes}</p>
        </div>
      </section>

      <section className="corridor-section paper">
        <div className="corridor-shell">
          <h2 className="display">Logistics — Mundra to {c.arrivalPort.split(",")[0]}</h2>
          <p>
            Containers stuff at <strong>{c.loadingPort}</strong> port in Gujarat. Sea transit to <strong>{c.arrivalPort}</strong> is{" "}
            <strong>{c.transitDays} days</strong>. End-to-end PI-to-warehouse lead time: <strong>{c.leadTimePIToWarehouse}</strong>.
            Payment structured as {c.paymentTerms}.
          </p>
          <p>
            Documentation packet (phytosanitary, FSSAI, APEDA, certificate of origin, Bill of Lading, independent lab COA, sortex pass log) is couriered within 48 hours of sail. We share live container tracking via the steamer line's portal until port-of-arrival sign-off.
          </p>
        </div>
      </section>

      <section className="corridor-section corridor-section--tint paper">
        <div className="corridor-shell">
          <h2 className="display">Why buyers in {c.country} choose Vilora Impex</h2>
          <ul className="corridor-ul">
            <li>FSSAI licensed, APEDA registered, Spices Board accredited, ISO 22000 certified — every export lot covered.</li>
            <li>Direct-from-mill sourcing in Punjab, Haryana and Andhra Pradesh — no broker layers, one accountable export desk.</li>
            <li>Two-pass sortex cleaning, hand-graded at origin, independent NABL-accredited lab COA per shipment.</li>
            <li>{c.country}-specific documentation handled upfront — {c.tradeAgreement.startsWith("India") ? "preferential COO under the bilateral framework, " : ""}halal where required, destination labelling reviewed pre-print for retail SKUs.</li>
            <li>Live container tracking from {c.loadingPort} to {c.arrivalPort.split(",")[0]} until your sign-off at destination.</li>
            <li>Documentation packet (phyto, FSSAI, APEDA, COO, BL, lab COA) couriered within 48 hours of sail.</li>
          </ul>
        </div>
      </section>

      <section className="corridor-section paper">
        <div className="corridor-shell">
          <h2 className="display">Frequently asked — exporting basmati to {c.country}</h2>
          <details className="corridor-faq">
            <summary>What is the minimum order quantity for basmati export to {c.country}?</summary>
            <p>{c.typicalMOQ}. LCL trial shipments of 5–10 MT accepted for new buyers under L/C or 100% advance.</p>
          </details>
          <details className="corridor-faq">
            <summary>What documentation will accompany the shipment?</summary>
            <p>
              Phytosanitary certificate, FSSAI license copy, APEDA registration copy, certificate of origin{c.tradeAgreement.startsWith("India") ? " (preferential form for the bilateral framework where eligible)" : ""}, original Bill of Lading, independent lab COA, sortex pass log, weighbridge slips and pre-stuffing photographs. {c.docExtras}.
            </p>
          </details>
          <details className="corridor-faq">
            <summary>What payment terms apply for shipments to {c.country}?</summary>
            <p>{c.paymentTerms}. For first shipments to a new buyer, 100% advance against a sample-approved Performa Invoice is standard.</p>
          </details>
          <details className="corridor-faq">
            <summary>Which basmati variety should we order for {c.country} retail?</summary>
            <p>{c.preferredVariety}. {c.marketNotes}</p>
          </details>
          <details className="corridor-faq">
            <summary>How long will it take from PI signing to my warehouse?</summary>
            <p>{c.leadTimePIToWarehouse}. Sea transit alone is {c.transitDays} days from {c.loadingPort} to {c.arrivalPort.split(",")[0]}.</p>
          </details>
        </div>
      </section>

      <section className="corridor-section corridor-section--cta paper">
        <div className="corridor-shell">
          <h2 className="display">Open a {c.country} basmati inquiry</h2>
          <p>
            Send your spec (variety, form, grade, packaging, quantity, destination port, target arrival date)
            to our export desk and we&apos;ll reply the same day in our timezone with price, lead time and the
            documentation we&apos;ll need to clear at {c.arrivalPort.split(",")[0]}.
          </p>
          <div className="corridor-actions">
            <a href="mailto:exports@viloraimpex.com" className="btn-primary">
              <span className="btn-code">EXPORT</span>
              <span>exports@viloraimpex.com</span>
              <span className="btn-arrow"><Mail size={14} strokeWidth={1.6} /></span>
            </a>
            <DownloadBrochureButton variant="ghost" />
            <Link to="/" className="btn-ghost">
              <ArrowLeft size={14} strokeWidth={1.6} />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}

function CorridorStyles() {
  return (
    <style>{`
      .corridor-page { color: var(--ink); }
      .corridor-shell { max-width: 880px; margin: 0 auto; padding: 0 clamp(20px, 4vw, 40px); }
      .corridor-hero { padding: clamp(40px, 5vw, 64px) 0 clamp(28px, 4vw, 44px); }
      .corridor-breadcrumb {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--ink-mute);
        display: flex; gap: 10px; align-items: center; flex-wrap: wrap;
        margin-bottom: 18px;
      }
      .corridor-breadcrumb a { color: var(--brass-deep); text-decoration: none; }
      .corridor-folio { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
      .corridor-flag-row { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
      .corridor-flag { font-size: 40px; line-height: 1; }
      .corridor-eyebrow { display: inline-block; }
      .corridor-h1 {
        font-size: clamp(32px, 5vw, 56px);
        line-height: 1.04;
        margin: 0 0 18px;
        color: var(--ink);
        letter-spacing: -0.005em;
      }
      .corridor-standfirst {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(17px, 1.7vw, 20px);
        line-height: 1.55;
        color: var(--ink-soft);
        margin: 0 0 22px;
      }
      .corridor-meta-row {
        display: flex; flex-wrap: wrap; gap: 16px 24px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 11.5px; letter-spacing: 0.12em; text-transform: uppercase;
        color: var(--ink-mute);
        margin-bottom: 24px;
      }
      .corridor-meta-item { display: inline-flex; align-items: center; gap: 7px; }
      .corridor-actions { display: flex; flex-wrap: wrap; gap: 12px; }

      .corridor-fact-grid {
        padding: clamp(28px, 4vw, 44px) 0;
        background: linear-gradient(180deg, rgba(168,132,61,0.06), rgba(168,132,61,0)), var(--paper-1, #FAF6EA);
      }
      .corridor-facts {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 18px 28px;
        margin: 0;
      }
      .corridor-fact {
        border-left: 2px solid var(--brass, #A8843D);
        padding-left: 14px;
      }
      .corridor-fact dt {
        font-family: 'JetBrains Mono', monospace;
        font-size: 10.5px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--brass-deep, #846530);
        margin-bottom: 4px;
      }
      .corridor-fact dd {
        font-family: 'Crimson Pro', serif;
        font-size: 15.5px;
        line-height: 1.42;
        color: var(--ink);
        margin: 0;
      }

      .corridor-section { padding: clamp(32px, 4vw, 52px) 0; }
      .corridor-section--tint {
        background: linear-gradient(180deg, rgba(168,132,61,0.04), rgba(168,132,61,0)), var(--paper-2, #ECE3CE);
      }
      .corridor-section h2 {
        font-family: 'Cormorant Garamond', serif;
        font-weight: 700;
        font-size: clamp(24px, 2.8vw, 32px);
        line-height: 1.1;
        color: var(--ink);
        margin: 0 0 14px;
      }
      .corridor-section p {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(16px, 1.5vw, 18px);
        line-height: 1.65;
        color: var(--ink-soft);
        margin: 0 0 14px;
      }
      .corridor-section p:last-child { margin-bottom: 0; }
      .corridor-ul {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(16px, 1.5vw, 18px);
        line-height: 1.6;
        color: var(--ink-soft);
        padding-left: 22px;
        margin: 0;
      }
      .corridor-ul li { margin-bottom: 10px; }

      .corridor-faq {
        border-top: 1px solid rgba(168,132,61,0.24);
        padding: 14px 0;
      }
      .corridor-faq:last-child { border-bottom: 1px solid rgba(168,132,61,0.24); }
      .corridor-faq summary {
        font-family: 'Cormorant Garamond', serif;
        font-weight: 600;
        font-size: clamp(17px, 1.6vw, 19px);
        color: var(--ink);
        cursor: pointer;
        list-style: none;
        position: relative;
        padding-right: 28px;
      }
      .corridor-faq summary::-webkit-details-marker { display: none; }
      .corridor-faq summary::after {
        content: '+'; position: absolute; right: 4px; top: 0;
        font-family: 'JetBrains Mono', monospace;
        font-size: 18px; color: var(--brass, #A8843D);
        transition: transform 200ms ease;
      }
      .corridor-faq[open] summary::after { content: '–'; }
      .corridor-faq p {
        margin: 10px 0 0;
        font-family: 'Crimson Pro', serif;
        font-size: 16px;
        line-height: 1.6;
        color: var(--ink-soft);
      }

      .corridor-section--cta {
        background: linear-gradient(180deg, rgba(168,132,61,0.06), rgba(168,132,61,0)), var(--paper-2, #ECE3CE);
        padding: clamp(40px, 5vw, 64px) 0 clamp(56px, 7vw, 88px);
      }
    `}</style>
  );
}
